import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {AxiosError} from "axios";
import {IAxiosApiError} from "data/types/api";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {ICrunchApiProvider} from "data/providers/api/сrunch.api.provider";

export interface ICrunchStore {
	get getIsLoading(): boolean;
	get selectedCrunch(): number | null;
	get isChanged(): boolean;

	updateCrunch(newCrunch: number): void;
	fetchCrunch(roundId: number): Promise<void>;
	saveCrunch(): Promise<void>;
}

@injectable()
export class CrunchStore implements ICrunchStore {
	@observable private _isLoading: boolean = false;
	@observable private _isChanged: boolean = false;

	constructor(
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.CrunchApiProvider) private _crunchApiProvider: ICrunchApiProvider,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	@observable private _crunch: number | null = null;

	get selectedCrunch() {
		return this._crunch;
	}

	get getIsLoading(): boolean {
		return this._isLoading;
	}

	get isChanged(): boolean {
		return this._isChanged;
	}

	protected onError(e: unknown) {
		const error = e as AxiosError<IAxiosApiError, unknown>;
		this._modalsStore.showAxiosError(error);
	}

	@action
	public updateCrunch(newCrunch: number): void {
		this._isChanged = true;
		this._crunch = newCrunch;
	}

	@action
	public async fetchCrunch(roundId: number): Promise<void> {
		if (!roundId || this._isLoading) {
			return;
		}

		try {
			this._isLoading = true;
			this._isChanged = false;
			const {data} = await this._crunchApiProvider.get(roundId);
			runInAction(() => {
				this._crunch = data.success.crunch;
			});
		} catch (e) {
			this._isLoading = false;
			this.onError(e);
			return Promise.reject(e);
		} finally {
			this._isLoading = false;
		}
	}

	@action
	public async saveCrunch(): Promise<void> {
		try {
			this._isLoading = true;
			this._isChanged = false;
			const {data} = await this._crunchApiProvider.save({
				crunch: this.selectedCrunch,
				round: this._roundsStore.currentSelectedRound?.id || 0,
			});
			runInAction(() => {
				this._crunch = data.success.crunch;
			});
		} catch (e) {
			this._isLoading = false;
			this.onError(e);
			return Promise.reject(e);
		} finally {
			this._isLoading = false;
		}
	}
}
